import { useQuery, useInfiniteQuery, useQueryClient, useMutation } from '@tanstack/react-query'
import { DocumentSummary, DocumentSearchParams } from '../types/models'
import { useAuth } from '../auth/AuthContext'
import { User } from 'firebase/auth'

const API_BASE = import.meta.env.DEV ? '/api' : 'https://asia-northeast1-bluepaper2.cloudfunctions.net/api'

interface DocumentSearchResponse {
  items: DocumentSummary[]
  pagination: {
    page: number
    limit: number
    total: number
    hasMore: boolean
    nextPage?: number
  }
  meta: {
    executionTimeMs: number
    totalTimeMs: number
    query?: string
    filters: any
  }
}

async function fetchDocuments(params: DocumentSearchParams, user: User, orgId: string): Promise<DocumentSearchResponse> {
  const token = await user.getIdToken()
  const searchParams = new URLSearchParams()

  if (params.query) searchParams.set('query', params.query)
  if (params.category) searchParams.set('category', params.category)
  if (params.folderId) searchParams.set('folderId', params.folderId)
  if (params.bookmarked) searchParams.set('bookmarked', 'true')
  if (params.status) searchParams.set('status', params.status)
  if (params.from) searchParams.set('from', params.from.toISOString())
  if (params.to) searchParams.set('to', params.to.toISOString())
  if (params.participants) params.participants.forEach(p => searchParams.append('participants', p))
  if (params.page) searchParams.set('page', params.page.toString())
  if (params.limit) searchParams.set('limit', params.limit.toString())
  if (params.sortBy) searchParams.set('sortBy', params.sortBy)
  if (params.sortOrder) searchParams.set('sortOrder', params.sortOrder)
  if (params.meetingOnly) searchParams.set('meetingOnly', 'true')
  if (params.excludeOwn) searchParams.set('excludeOwn', 'true')
  if (params.ownOnly) searchParams.set('ownOnly', 'true')

  const url = `${API_BASE}/v1/organizations/${orgId}/documents?${searchParams}`

  console.log('🔍 Fetching documents with params:', {
    folderId: params.folderId,
    bookmarked: params.bookmarked,
    url,
    searchParams: Object.fromEntries(searchParams.entries())
  })
  
  const response = await fetch(url, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  })

  if (!response.ok) {
    let errorMessage = `Search failed: ${response.status} ${response.statusText}`
    try {
      const errorData = await response.json()
      if (errorData.error) {
        errorMessage += ` - ${errorData.error}`
      }
    } catch (e) {}
    throw new Error(errorMessage)
  }

  return response.json()
}

export function useDocuments(params: DocumentSearchParams) {
  const { user, firebaseUser } = useAuth()

  return useQuery({
    queryKey: ['documents', user?.orgId, params],
    queryFn: async () => {
      if (!firebaseUser || !user?.orgId) throw new Error('Not authenticated or orgId is missing')
      return fetchDocuments(params, firebaseUser, user.orgId)
    },
    enabled: !!user && !!user.orgId && !!firebaseUser,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false
  })
}

export function useInfiniteDocuments(params: Omit<DocumentSearchParams, 'page'>) {
  const { user, firebaseUser } = useAuth()

  // queryキーに含めるために、undefinedや空の値を除外したparamsを作成
  const normalizedParams = Object.fromEntries(
    Object.entries(params).filter(([_, value]) => value !== undefined && value !== null && value !== '')
  )

  console.log('🔍 useInfiniteDocuments called with:', {
    params,
    normalizedParams,
    folderId: params.folderId,
    bookmarked: params.bookmarked
  })

  return useInfiniteQuery({
    queryKey: ['documents-infinite', user?.orgId, normalizedParams],
    queryFn: async ({ pageParam = 1 }) => {
      if (!firebaseUser || !user?.orgId) throw new Error('Not authenticated or orgId is missing')
      console.log('🔍 Fetching page:', { pageParam, params })
      return fetchDocuments({ ...params, page: pageParam as number }, firebaseUser, user.orgId)
    },
    enabled: !!user && !!user.orgId && !!firebaseUser,
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage?.pagination?.hasMore ? lastPage.pagination.nextPage : undefined,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
    retry: (failureCount, error) => {
      if (error.message.includes('authenticate') || error.message.includes('401')) return false
      return failureCount < 2
    }
  })
}

export function useCreateDocument() {
  const { user, firebaseUser } = useAuth()
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: async (data: { /* ... */ }) => {
      if (!firebaseUser || !user?.orgId) throw new Error('Not authenticated or orgId is missing')

      console.log('📝 Creating document:', {
        orgId: user.orgId,
        data,
        apiBase: API_BASE
      })

      const token = await firebaseUser.getIdToken()
      
      // トークンの詳細をデバッグ
      const tokenResult = await firebaseUser.getIdTokenResult()
      console.log('🔐 Token details for API call:', {
        claims: tokenResult.claims,
        orgId: tokenResult.claims.orgId,
        role: tokenResult.claims.role,
        tokenLength: token.length
      })
      
      const response = await fetch(`${API_BASE}/v1/organizations/${user.orgId}/documents`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      })

      console.log('📝 Document creation response:', {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok
      })

      if (!response.ok) {
        const errorText = await response.text()
        throw new Error(`Document creation failed: ${response.status} - ${errorText}`)
      }

      const result = await response.json()
      console.log('📝 Document creation result:', result)
      
      // IDを適切に抽出
      if (typeof result === 'string') {
        return result
      }
      if (result.id) {
        return result.id
      }
      if (result.documentId) {
        return result.documentId
      }
      
      // 最後の手段として、resultがオブジェクトの場合はエラーを投げる
      throw new Error('Document ID not found in response')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents', user?.orgId] })
      queryClient.invalidateQueries({ queryKey: ['documents-infinite', user?.orgId] })
    },
    onError: (error) => console.error('Document creation error:', error)
  })

  return mutation
}

export function useToggleDocumentBookmark() {
  const { user, firebaseUser } = useAuth()
  const queryClient = useQueryClient()

  const toggleBookmarkMutation = useMutation({
    mutationFn: async ({ docId, isBookmarked }: { docId: string, isBookmarked: boolean }) => {
      if (!firebaseUser || !user?.orgId) throw new Error('Not authenticated or orgId is missing')

      console.log('🔖 Starting bookmark operation:', { docId, isBookmarked, orgId: user.orgId });

      const token = await firebaseUser.getIdToken();
      const response = await fetch(`${API_BASE}/v1/organizations/${user.orgId}/documents/${docId}/bookmark`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ bookmarked: isBookmarked })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      return response.json();
    },
    onMutate: async ({ docId, isBookmarked }) => {
      console.log(' optimistically updating cache', { docId, isBookmarked });
      await queryClient.cancelQueries({ queryKey: ['documents-infinite', user?.orgId] });

      // ロールバック用に、関連するすべてのキャッシュの現在の状態をスナップショット
      const previousQueriesData = queryClient.getQueriesData({ queryKey: ['documents-infinite', user?.orgId], exact: false });

      // キャッシュを楽観的に更新
      queryClient.setQueriesData({ queryKey: ['documents-infinite', user?.orgId], exact: false }, (oldData: any) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          pages: oldData.pages.map((page: any) => ({
            ...page,
            items: page.items.map((item: any) =>
              item.id === docId ? { ...item, bookmarked: isBookmarked } : item
            ),
          })),
        };
      });

      // スナップショットをコンテキストとして返す
      return { previousQueriesData };
    },
    onError: (err, variables, context) => {
      console.error('Bookmark mutation failed, rolling back optimistic update', { err });
      // 失敗した場合、onMutateから返されたスナップショットを使ってキャッシュを元に戻す
      if (context?.previousQueriesData) {
        context.previousQueriesData.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },
    onSettled: (data, error, { docId }) => {
      console.log('Settled: refetching data for final consistency', { docId });
      // 成功・失敗にかかわらず、最終的にサーバーとデータを同期させる
      queryClient.invalidateQueries({ queryKey: ['documents-infinite', user?.orgId], exact: false });
      queryClient.invalidateQueries({ queryKey: ['document', docId] });
    },
  });

  return toggleBookmarkMutation
}